import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../index";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "./CardView.css";

// Cache configuration
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours
const CACHE_KEY = "tournamentCache";
const CACHE_KEY_PRICES = "cardPricesCache";

const CardView = () => {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [representation, setRepresentation] = useState([]);
  const [averagePrice, setAveragePrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [storePrices, setStorePrices] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchTournamentData = async () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        if (Date.now() - parsedCache.timestamp < CACHE_TTL) {
          return parsedCache.data;
        }
      }

      const response = await fetch("/tournaments.json");
      const data = await response.json();

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data, timestamp: Date.now() })
      );

      return data;
    } catch (error) {
      console.error("Error fetching tournament data:", error);
      return [];
    }
  };

  const fetchCardData = async () => {
    try {
      const [baseId, rarity] = id.split("_");

      const cardRef = doc(db, "cards", baseId);
      const cardSnapshot = await getDoc(cardRef);

      if (!cardSnapshot.exists()) {
        setError("Card not found.");
        return;
      }

      const cardData = cardSnapshot.data();
      setCard({ ...cardData, id: baseId, rarity });

      let cachedPrices = localStorage.getItem(CACHE_KEY_PRICES);
      let storePricesData;

      if (cachedPrices) {
        const { timestamp, data } = JSON.parse(cachedPrices);
        if (Date.now() - timestamp < CACHE_TTL && data[`${baseId}_${rarity}`]) {
          console.log("Using cached price data.");
          storePricesData = data[`${baseId}_${rarity}`];
        }
      }

      if (!storePricesData) {
        console.log("Fetching fresh price data.");
        const pricesRef = doc(db, `cards/${baseId}/prices`, rarity);
        const pricesSnapshot = await getDoc(pricesRef);

        if (pricesSnapshot.exists()) {
          storePricesData = pricesSnapshot.data();

          const updatedCache = cachedPrices
            ? {
                ...JSON.parse(cachedPrices).data,
                [`${baseId}_${rarity}`]: storePricesData,
              }
            : { [`${baseId}_${rarity}`]: storePricesData };

          localStorage.setItem(
            CACHE_KEY_PRICES,
            JSON.stringify({ timestamp: Date.now(), data: updatedCache })
          );
        } else {
          setAveragePrice(null);
          setStorePrices([]);
          setPriceHistory([]);
          return;
        }
      }

      let latestPrices = [];
      let priceHistories = {};
      let storeData = [];

      Object.keys(storePricesData).forEach((store) => {
        if (storePricesData[store].latestPrice) {
          const latest = storePricesData[store].latestPrice;
          latestPrices.push(latest.price);
          storeData.push({ store, ...latest });
        }
        if (storePricesData[store].priceHistory) {
          storePricesData[store].priceHistory.forEach((entry) => {
            const date = new Date(entry.timestamp).toISOString().split("T")[0];
            if (!priceHistories[date]) {
              priceHistories[date] = [];
            }
            priceHistories[date].push(entry.price);
          });
        }
      });

      const seenDates = new Set();
      const today = new Date().toISOString().split("T")[0];
      if (latestPrices.length > 0) {
        if (!priceHistories[today]) {
          priceHistories[today] = [];
        }
        priceHistories[today].push(...latestPrices);
      }
      const averagedPriceHistory = Object.entries(priceHistories)
        .map(([date, prices]) => ({
          date: new Date(date),
          average: parseFloat(
            (prices.reduce((sum, price) => sum + price, 0) / prices.length).toFixed(2)
          ),
        }))
        .filter((entry) => {
          if (seenDates.has(entry.date.toISOString())) return false;
          seenDates.add(entry.date.toISOString());
          return true;
        })
        .sort((a, b) => a.date - b.date);

      setPriceHistory(averagedPriceHistory);

      if (latestPrices.length > 0) {
        const average =
          latestPrices.reduce((sum, price) => sum + price, 0) / latestPrices.length;
        setAveragePrice(average.toFixed(2));
      } else {
        setAveragePrice(null);
      }

      setStorePrices(storeData);

      const tournamentsData = await fetchTournamentData();

      const repData = tournamentsData.reduce((acc, tournament) => {
        tournament.decklists.forEach((deck) => {
          let isCardInDeck = false;

          if (deck.oshi && deck.oshi.startsWith(baseId)) {
            isCardInDeck = true;
          }

          if (!isCardInDeck && Array.isArray(deck.main)) {
            isCardInDeck = deck.main.some((entry) => {
              if (typeof entry === "object" && entry !== null) {
                return Object.keys(entry).some(
                  (key) => key === `${baseId}_${rarity}`
                );
              }
              return false;
            });
          }

          if (!isCardInDeck && Array.isArray(deck.cheers)) {
            isCardInDeck = deck.cheers.some((entry) => {
              if (typeof entry === "object" && entry !== null) {
                return Object.keys(entry).some(
                  (key) => key === `${baseId}_${rarity}`
                );
              }
              return false;
            });
          }

          if (isCardInDeck) {
            acc[deck.archetype] = (acc[deck.archetype] || 0) + 1;
          }
        });
        return acc;
      }, {});

      const formattedRep = Object.entries(repData).map(
        ([archetype, count]) => ({
          archetype,
          count,
        })
      );

      setRepresentation(formattedRep);
    } catch (error) {
      console.error("Error fetching card data:", error);
      setError("Failed to load card data. Please try again later.");
    }
  };

  useEffect(() => {
    fetchCardData();
  }, [id]);

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  if (!card) {
    return <p>Loading card details...</p>;
  }

  const imageName = `${card.id}_${card.rarity}.png`;

  const graphData = {
    labels: priceHistory.map((entry) => entry.date.toLocaleDateString("en-US")),
    datasets: [
      {
        label: "Average Price History (USD)",
        data: priceHistory.map((entry) => entry.average),
        fill: false,
        borderColor: "#3498db",
        tension: 0.1,
      },
    ],
  };

  const graphOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Date",
        },
      },
      y: {
        title: {
          display: true,
          text: "Price (USD)",
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="card-view-container">
      <button className="back-button" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="card-view-content">
        <div className="card-image-section">
          <img
            src={`/images/${imageName}`}
            alt={card.name}
            className="card-image"
            onError={(e) => (e.target.src = "/images/default.png")}
          />
        </div>

        <div className="card-details-section">
          <div className="card-header">
            <h1 className="card-name">{card.name}</h1>
            <div className="card-average-price">
              {averagePrice ? `US$ ${averagePrice}` : "N/A"}
            </div>
          </div>
          <div className="card-detail">
            <strong>ID:</strong> {card.id}
          </div>
          <div className="card-detail">
            <strong>Type:</strong> {card.type || "N/A"}
          </div>
          <div className="card-detail">
            <strong>Rarity:</strong> {card.rarity || "N/A"}
          </div>
          <div className="card-detail">
            <strong>Color:</strong> {card.color || "N/A"}
          </div>
          <div className="card-text">
            <strong>Text:</strong>
            <p>
              {card.text?.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              )) || "No description available."}
            </p>
          </div>
        </div>

        <div className="card-meta-section">
          <div className="card-meta-block">
            <h3>Store Prices</h3>
            {storePrices.length > 0 ? (
              <ul>
                {storePrices.map((store, index) => (
                  <li key={index}>
                    <strong>{store.store}:</strong> US$ {store.price}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No store prices available.</p>
            )}
          </div>
          <div className="card-meta-block">
            <h3>Representation</h3>
            {representation.length > 0 ? (
              <ul>
                {representation.map((rep, index) => (
                  <li key={index}>
                    {rep.archetype} - {rep.count} decks
                  </li>
                ))}
              </ul>
            ) : (
              <p>No representation data available.</p>
            )}
          </div>
          <div className="card-meta-block">
            <h3>Restricted</h3>
            <ul>
              <li>{card.restricted ? "Yes" : "No"}</li>
            </ul>
          </div>
        </div>

        <div className="card-price-history">
          <h3>Price History</h3>
          {priceHistory.length > 0 ? (
            <Line data={graphData} options={graphOptions} />
          ) : (
            <p>No price history available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardView;
